import { Injectable } from '@angular/core';
import { environment } from '../../../environments/default/environment';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { from, map, of, Subject, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { MsalService } from '@azure/msal-angular';

const API_URL = environment.apiUrl;
const IMPERSONATION = 'impersonation';

@Injectable({
    providedIn: 'root'
})
export class ApiService {
    private onHttpError = new Subject<HttpErrorResponse>();

    constructor(
        private http: HttpClient,
        private authService: MsalService
    ) {}

    handleError(err) {
        setTimeout(() => {
            this.onHttpError.next(err);
        });
        return throwError(err);
    }

    async get(url: string, options?: any): Promise<any> {
        const httpOptions = await this.getHttpOptions(options);
        return this.http.get(API_URL + url, httpOptions).toPromise();
    }

    async getExternalWithBearer(url: string, options?: any): Promise<any> {
        const httpOptions = await this.getHttpOptionsExternalWithBearer(options);
        return this.http.get(url, httpOptions).toPromise();
    }

    async post(url: string, body?: any, options?: any): Promise<any> {
        const httpOptions = await this.getHttpOptions(options);
        return this.http.post<any>(API_URL + url, body ?? {}, httpOptions).toPromise();
    }

    async patch(url: string, body?: any, options?: any): Promise<any> {
        const httpOptions = await this.getHttpOptions(options);
        return this.http.patch<any>(API_URL + url, body, httpOptions).toPromise();
    }

    async put(url: string, body?: any, options?: any): Promise<any> {
        const httpOptions = await this.getHttpOptions(options);
        return this.http.put<any>(API_URL + url, body, httpOptions).toPromise();
    }

    putExternal(url: string, body?: any, options?: any): Promise<any> {
        const httpOptions = this.getHttpOptionsExternal(options);
        return this.http.put<any>(url, body, httpOptions).toPromise();
    }

    async putExternalWithBearer(url: string, body?: any, options?: any): Promise<any> {
        const httpOptions = await this.getHttpOptionsExternalWithBearer(options);
        return this.http.put<any>(url, body, httpOptions).toPromise();
    }

    async getExternal(url: string, options?: any): Promise<any> {
        const httpOptions = await this.getHttpOptionsExternal(options);
        return this.http.get<any>(url, httpOptions).toPromise();
    }

    patchExternal(url: string, body?: any, options?: any): Promise<any> {
        const httpOptions = this.getHttpOptionsExternal(options);
        return this.http.patch<any>(url, body, httpOptions).toPromise();
    }

    postExternal(url: string, body?: any, options?: any): Promise<any> {
        const httpOptions = this.getHttpOptionsExternal(options);
        return this.http.post<any>(url, body, httpOptions).toPromise();
    }

    deleteExternal(url: string, options?: any): Promise<any> {
        const httpOptions = this.getHttpOptionsExternal(options);
        return this.http.delete<any>(url, httpOptions).toPromise();
    }

    async delete(url: string, options?: any): Promise<any> {
        const httpOptions = await this.getHttpOptionsDelete(options);
        return this.http
            .delete(API_URL + url, httpOptions)
            .pipe(
                catchError((err, caught) => {
                    if (err instanceof HttpErrorResponse) {
                        throw this.handleError(err);
                    }
                    throw err;
                })
            )
            .toPromise();
    }

    private getToken(): Promise<string> {
        const request = {
            scopes: environment.apiConfig[1].scopes,
            account: this.authService.instance.getActiveAccount() ?? this.authService.instance.getAllAccounts()[0]
        };

        return from(this.authService.instance.acquireTokenSilent(request))
            .pipe(
                map(result => result.accessToken),
                catchError(error => {
                    console.warn('Silent token acquisition failed, redirecting for login...', error);
                    return from(this.authService.instance.acquireTokenRedirect(request)).pipe(
                        map(() => null as string),
                        catchError(redirectError => {
                            console.error('Redirect token acquisition failed', redirectError);
                            return of(null as string);
                        })
                    );
                })
            )
            .toPromise();
    }

    private async getHttpOptions(options?: any, excludeImpersonation = false) {
        const impersonation = localStorage.getItem(IMPERSONATION);
        const httpOptions = { ...options };
        const headers = {
            Authorization: `Bearer ${await this.getToken()}`,
            'Content-Type': 'application/json; charset=utf-8'
        };

        if (impersonation && !excludeImpersonation) {
            (headers as any).Impersonation = impersonation ? JSON.parse(impersonation) : '';
        }
        httpOptions.headers = { ...headers, ...(options ? options.headers : {}) };
        return httpOptions;
    }

    private async getScdHttpOptions(options?: any) {
        const httpOptions = { ...options };
        const headers = {
            Authorization: `Bearer ${await this.getToken()}`,
            'x-api-key': environment.scdApiKey,
            'Content-Type': 'application/json; charset=utf-8'
        };

        httpOptions.headers = { ...headers, ...(options ? options.headers : {}) };
        return httpOptions;
    }

    private getHttpOptionsExternal(options?: any) {
        const httpOptions = { ...options };
        const headers = {
            'Content-Type': 'application/json; charset=utf-8'
        };

        httpOptions.headers = { ...headers, ...(options ? options.headers : {}) };
        return httpOptions;
    }

    private async getHttpOptionsExternalWithBearer(options?: any) {
        const httpOptions = { ...options };
        const headers = {
            Authorization: `Bearer ${await this.getToken()}`
        };

        httpOptions.headers = { ...headers, ...(options ? options.headers : {}) };
        return httpOptions;
    }

    private async getHttpOptionsDelete(options?: any) {
        const httpOptions = { ...options };
        const headers = {
            Authorization: `Bearer ${await this.getToken()}`
        };
        httpOptions.headers = { ...headers, ...(options ? options.headers : {}) };
        return httpOptions;
    }
}
